import { useCallback, useEffect, useRef, useState } from "react";
import { errorTextColor, successTextColor, warningTextColor } from "styles/main";
import { readBarcodes } from "zxing-wasm/reader";
import qz from 'qz-tray';
import { get, post } from "api/apiBridges/httpBridge";
// import webPrintPdf from 'web-print-pdf';

export const scanPageForKiz = async (page) => {
    console.log("page", page)
    const viewport = page.getViewport({ scale: 2 });

    const canvas = document.createElement("canvas");
        /** @type {any} */
    const context = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
        canvasContext: context,
        viewport,
    }).promise;

    const imageData = context.getImageData(
        0,
        0,
        canvas.width,
        canvas.height
    );

    const results = await readBarcodes(imageData, {
        formats: ["DataMatrix"],
        tryHarder: true,
        maxNumberOfSymbols: 20,
    });

    return results;
};

export function formatGS1Code(input) {
    const regex = /\((\d+)\)([^()]*)/g;
    const groups = [];
    let match;

    // Извлекаем все (AI)значение
    while ((match = regex.exec(input)) !== null) {
        const ai = match[1];
        const value = match[2];
        groups.push(ai + value);
    }

    // Соединяем группы символом GS (ASCII 29)
    const GS = String.fromCharCode(29);
    const result = groups.join(GS);

    // Добавляем FNC1 в начало, если нужно
    // return String.fromCharCode(232) + result;
    return result;
}

//предпологаемая длина символов
export const EXPECTED_LENGTHS = {
    '01': 14,
    '21': 13,
    '91': 4,
    '92': 44,
};

export const addGSSeparators = (code) => {
    const GS = String.fromCharCode(29);
    const FNC1 = String.fromCharCode(232);

    // Убираем уже существующие GS, чтобы функция была идемпотентной
    let s = code.replace(/\x1D/g, '');

    // После FNC1 должен идти AI 01
    if (s.slice(0, 2) !== '01') {
      throw new Error('Не найден AI 01');
    }

    // GTIN = 14 цифр
    const gtin = s.slice(2, 16);

    if (!/^\d{14}$/.test(gtin)) {
      throw new Error('Некорректный GTIN');
    }

    // После GTIN должен идти AI 21
    if (s.slice(16, 18) !== '21') {
      throw new Error('Не найден AI 21');
    }

    // Ищем AI 91 и AI 92
    const pos91 = s.indexOf('91', 18);
    if (pos91 === -1) {
      throw new Error('Не найден AI 91');
    }

    const pos92 = s.indexOf('92', pos91 + 2);
    if (pos92 === -1) {
      throw new Error('Не найден AI 92');
    }

    // Между 21 и 91 находится серийный номер
    const serial = s.slice(18, pos91);

    // AI 91 + 4 символа ключа
    const key = s.slice(pos91 + 2, pos91 + 6);

    if (key.length !== 4) {
      throw new Error('Ключ AI 91 должен содержать 4 символа');
    }

    // После 91XXXX должен начинаться AI 92
    if (s.slice(pos91 + 6, pos91 + 8) !== '92') {
      throw new Error('Некорректная структура после AI 91');
    }

    // Криптохвост
    const cryptoTail = s.slice(pos92 + 2);

    if (!cryptoTail) {
      throw new Error('Пустой криптохвост');
    }

    // Собираем код заново с GS
    return (
      // FNC1 +
      '01' +
      gtin +
      '21' +
      serial +
      GS +
      '91' +
      key +
      GS +
      '92' +
      cryptoTail
    );
}

// ----- Парсинг кода по секциям -----
export const parseCode = (code) => {
    const result = {
        '01': null,
        '21': null,
        '91': null,
        '92': null
    };

    // 01 — первые 14 цифр после AI
    if (code.startsWith('01') && code.length >= 16) {
        result['01'] = code.substring(2, 16);
    }

    // Всё после 01
    let rest = code.substring(16);

    // 21 — переменное поле до GS
    if (rest.startsWith('21')) {
        rest = rest.substring(2);

        const separatorIndex = rest.indexOf('\x1D');
        console.log("separatorIndex", separatorIndex)

        if (separatorIndex !== -1) {
            result['21'] = rest.substring(0, separatorIndex);
            rest = rest.substring(separatorIndex + 1);
        } else {
            // если GS отсутствует — ищем 91
            const idx91 = rest.indexOf('91');

            if (idx91 !== -1) {
                result['21'] = rest.substring(0, idx91);
                rest = rest.substring(idx91);
            } else {
                result['21'] = rest;
                rest = '';
            }
        }
    }

    // 91 — фиксированные 4 символа
    if (rest.startsWith('91')) {
        result['91'] = rest.substring(2, 6);
        rest = rest.substring(6);

        // после 91 может идти GS
        if (rest.startsWith('\x1D')) {
            rest = rest.substring(1);
        }
    }

    // 92 — всё оставшееся
    if (rest.startsWith('92')) {
        result['92'] = rest.substring(2);
    }

    return result;
};

export const getSectionColor = (key, value) => {
    // Если секция отсутствует
    if (value === null) {
      const isCritical = key === '01' || key === '21';

      return isCritical ? errorTextColor : warningTextColor;
    }

    return successTextColor;    
};

export const getSectionLabel = (key) => {
    const map = {
      '01': 'GTIN',
      '21': 'ИСН',
      '91': 'Идентификатор',
      '92': 'Код проверки',
    };

    return map[key] || key;
};

export const getSectionDisplay = (key, value) => {
    const expected = EXPECTED_LENGTHS[key];

    if (value === null) {
      return '_'.repeat(expected);
    }

    if (value.length < expected) {
      return value + '_'.repeat(expected - value.length);
    }

    return value; 
};

export const useSound = (successPath, errorPath) => {
    /** @type {any} */
    const successSound = useRef(null);
    /** @type {any} */
    const errorSound = useRef(null);

    useEffect(() => {
        successSound.current = new Audio(successPath);
        errorSound.current = new Audio(errorPath);
        successSound.current.volume = 0.7;
        errorSound.current.volume = 0.7;
    }, [successPath, errorPath]);

    const playSuccess = useCallback(() => {
        if (successSound.current) {
            successSound.current.currentTime = 0;
            successSound.current.play().catch(() => {});
        }
    }, []);

    const playError = useCallback(() => {
        if (errorSound.current) {
            errorSound.current.currentTime = 0;
            errorSound.current.play().catch(() => {});
        }
    }, []);

    return { playSuccess, playError };
};

// Целая песня с сертификатами на бесплатной версии
export function useQzTray() {
    const securityConfigured = useRef(false); //Чтобы не рендрилась каждый раз

    //Чтобы не перересовывались зависимости
    const setupSecurity = useCallback(() => {
        if (securityConfigured.current) return;
        securityConfigured.current = true;

        qz.security.setCertificatePromise((resolve, reject) => {
            get("auth/qz/certificate", undefined, true)
                .then(resolve)
                .catch((error) => {
                    console.error("QZ certificate loading error:", error);
                    reject(error);
                });
        });

        qz.security.setSignatureAlgorithm("SHA512");

        qz.security.setSignaturePromise((toSign) => {
            return (resolve, reject) => {
                post("auth/qz/sign", { data: toSign }, true)
                    .then(resolve)
                    .catch((error) => {
                        console.error("QZ signing error:", error);
                        reject(error);
                    });
            };
        });
    }, []);

    const connect = useCallback(async () => {
        setupSecurity();
        if (!qz.websocket.isActive()) {
            await qz.websocket.connect({ usingSecure: false });
        }
    }, [setupSecurity]);

    const getDefaultPrinter = useCallback(async () => {
        await connect();
        return await qz.printers.getDefault();
    }, [connect]);

    const printLocal = useCallback(async (data, options = {}) => {
        await connect();
        const printer = options.printer || await qz.printers.getDefault();
        const config = qz.configs.create(printer, {
            size: options.size,
            units: options.units || "mm",
            orientation: options.orientation || "portrait",
            margins: options.margins ?? 0,
            scaleContent: options.scaleContent ?? true,
            rasterize: options.rasterize ?? true,
        });
        return await qz.print(config, data);
    }, [connect]);

    return { connect, getDefaultPrinter, printLocal };
}

// https://github.com/qzind/tray/releases/download/v2.2.6/qz-tray-2.2.6-x86_64.exe
